import { useTranslation } from 'hooks/useTranslation';
import React from 'react';
import { Forms } from 'shared';

const useValidation = () => {
  const { t } = useTranslation('validation');

  const required = React.useMemo(
    () => ({
      required: Forms.validators.required(t('required')),
    }),
    [t]
  );

  const requiredArray = React.useMemo(
    () => ({
      required: Forms.validators.requiredArray(t('required')),
    }),
    [t]
  );

  const code = React.useMemo(
    () => ({
      length: Forms.validators.minMaxLength({ min: 4, max: 4 }, t('validationPhoneCode')),
    }),
    [t]
  );

  const username = React.useMemo(
    () => ({
      length: Forms.validators.minMaxLength({ min: 1, max: 50 }, t('usernameLength')),
      username: Forms.validators.username(t('username')),
    }),
    [t]
  );

  const password = React.useMemo(
    () => ({
      minLength: Forms.validators.minMaxLength({ min: 8 }, t('passwordSignUp')),
      oneUppercase: Forms.validators.atLeastOneUpperCase(t('passwordSignUp')),
      oneLowercase: Forms.validators.atLeastOneLowerCase(t('passwordSignUp')),
      oneDigit: Forms.validators.atLeastOneDigit(t('passwordSignUp')),
      oneSpacialCharacter: Forms.validators.atLeastOneNonWordCharacter(t('passwordOneNonWordCharacter')),
    }),
    [t]
  );

  const isEqual = React.useCallback((value?: string) => Forms.validators.equal(value, t('passwordsEquality')), [t]);

  const maxWordLength = React.useCallback(
    (max: number) => ({
      maxLength: Forms.validators.textMaxLength(max, t('maxLength')),
    }),
    [t]
  );

  const email = React.useMemo(
    () => ({
      email: Forms.validators.email(t('emailInvalid')),
    }),
    [t]
  );

  const minNumber = React.useMemo(
    () => (min: number) => ({
      minLength: Forms.validators.minNumber(t('minNumber'), min),
    }),
    [t]
  );

  const maxNumber = React.useMemo(
    () => (max: number) => ({
      max: Forms.validators.maxNumber(t('maxNumber'), max),
    }),
    [t]
  );

  const cvv = React.useMemo(
    () => ({
      minLength: {
        value: 3,
        message: t('cvvMinLength'),
      },
      maxLength: {
        value: 4,
        message: t('cvvMaxLength'),
      },
    }),
    [t]
  );

  const expiryDate = React.useMemo(
    () => (max: number) => ({
      max: Forms.validators.maxNumber(t('maxNumber'), max),
    }),
    [t]
  );

  const phoneNumber = React.useMemo(() => Forms.validators.phoneNumber(t('phoneNumber')), [t]);

  const isInteger = React.useMemo(() => Forms.validators.isInteger(t('isInteger')), [t]);

  const moneyValue = React.useMemo(
    () => Forms.validators.isFloat(t('amountMustHaveMaximumOfTwoDecimalPlaces'), 2),
    [t]
  );

  return {
    required,
    password,
    isEqual,
    username,
    code,
    requiredArray,
    maxWordLength,
    email,
    phoneNumber,
    isInteger,
    minNumber,
    maxNumber,
    cvv,
    expiryDate,
    moneyValue,
  };
};

export default useValidation;
